import { el, icon } from '../../lib/dom';
import { announce } from '../../lib/ds';

/**
 * Toast (DS `.toast-wrap` / `.toast`).
 *
 * ⚠️ **Slogi so DS, vedenje je naše — in to ni obvoz.** `window.toast()` živi v
 * `ds/js/components-advanced.js`, ki ga ne vendoriramo: datoteka nosi tudi
 * demo tabelo uporabnikov in izbirnik datuma (T1 §9). Vzeti iz nje samo eno
 * funkcijo bi pomenilo vendorirati demo kodo. Ista delitev kot pri modalu:
 * `.cm-scrim` je DS, `modal.ts` je naš.
 *
 * ---
 *
 * **Zakaj toast v tej aplikaciji sploh obstaja** (P2 §6.5): na iOS
 * `createMediaElementSource()` za žive tokove ne dela, zato ekvalizerja tam ni.
 * Zahtevana je **glasna odpoved, ne tiha** — uporabnik, ki premakne drsnike in
 * ne sliši ničesar, išče napako v svojih slušalkah.
 *
 * Nosi ga `role="status"` DS-a prek `a11y.announce()`, ker je pojasnilo in ne
 * prekinitev — nikoli `assertive` (P2 §3).
 */

/** Vrste iz DS: `success` · `error` · `warning` · `info`. */
export type ToastKind = 'success' | 'error' | 'warning' | 'info';

/** Koliko časa toast stoji. Ujema se s trajanjem `@keyframes toastbar` v DS. */
const VISIBLE_MS = 4000;
/** Trajanje odhoda; DS `.toast` ima prehod `var(--dur-base)`. */
const LEAVE_MS = 320;

const ICONS: Record<ToastKind, string> = {
  success: 'i-check',
  error: 'i-x',
  warning: 'i-alert',
  info: 'i-info',
};

export interface ToastHost {
  show(kind: ToastKind, title: string, body: string, closeLabel: string): void;
}

export function createToastHost(root: HTMLElement): ToastHost {
  const wrap = el('div', { class: 'toast-wrap', id: 'toastWrap' });
  root.append(wrap);

  return {
    show(kind, title, body, closeLabel) {
      const close = el('button', { class: 'cl', type: 'button', 'aria-label': closeLabel }, [
        icon('i-x', 14),
      ]);

      const toast = el('div', { class: `toast ${kind}` }, [
        el('span', { class: 'ic' }, [icon(ICONS[kind], 13)]),
        el('div', { class: 'body' }, [
          el('div', { class: 't' }, [title]),
          el('div', { class: 'm' }, [body]),
        ]),
        close,
        el('span', { class: 'bar' }),
      ]);
      wrap.append(toast);

      /**
       * Naznanilo gre skozi živo regijo DS-a in **ne** prek `aria-live` na
       * samem toastu: element nastane šele ob dogodku, bralnik zaslona pa
       * spremembe v regiji, ki je prej ni bilo, ne prebere.
       */
      announce(`${title}. ${body}`);

      // Razred `show` mora pasti v NASLEDNJI okvir, sicer brskalnik prehoda ne
      // izračuna — element je nastal in se preoblikoval v isti izračunani slog.
      requestAnimationFrame(() => {
        toast.classList.add('show');
      });

      let leaving: ReturnType<typeof setTimeout> | undefined;
      function dismiss(): void {
        clearTimeout(hide);
        clearTimeout(leaving);
        toast.classList.remove('show');
        leaving = setTimeout(() => {
          toast.remove();
        }, LEAVE_MS);
      }

      close.addEventListener('click', dismiss);
      const hide = setTimeout(dismiss, VISIBLE_MS);
    },
  };
}
